/**
 * The browser's call to the in-app guide: one question in, markdown out as it
 * is written.
 *
 * Two ways there, the same as every compliance read: straight to the domain
 * API with the short-lived bearer when the browser holds one, otherwise through
 * the workspace server's `/api/amoreg/guide/ask`, which pipes the same stream.
 * A direct attempt that cannot connect falls back to the proxy once, so a CORS
 * or network hiccup costs a moment rather than the answer.
 *
 * @module state/guideAsk
 * @see apps/api/src/guide.ts for what the guide knows and will not say
 */
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import {
  invalidateDomainApiSession,
  markDomainApiUnreachable,
  resolveComplianceRequest,
} from "./domainApi";

export interface GuideTurn {
  readonly role: "user" | "assistant";
  readonly content: string;
}

export interface GuideAskRequest {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly path: string;
  readonly messages: ReadonlyArray<GuideTurn>;
}

export type GuideFailure = "rate-limited" | "unavailable";

export class GuideAskError extends Error {
  constructor(readonly reason: GuideFailure) {
    super(reason);
    this.name = "GuideAskError";
  }
}

/** What the API accepts: recent turns only, each within its length bound. */
export function guidePayload(request: GuideAskRequest) {
  return {
    orgSegment: request.orgSegment,
    path: request.path.slice(0, 300),
    messages: request.messages
      .filter((turn) => turn.content.trim().length > 0)
      .slice(-24)
      .map((turn) => ({ role: turn.role, content: turn.content.slice(0, 4000) })),
  };
}

async function post(request: GuideAskRequest, signal: AbortSignal, forceProxy: boolean) {
  const proxyUrl = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/guide/ask", {
    organizationId: request.organizationId,
  });
  const resolved = await resolveComplianceRequest(
    forceProxy
      ? proxyUrl
      : {
          proxyUrl,
          apiPath: `/api/v1/organizations/${encodeURIComponent(request.organizationId)}/guide/ask`,
        },
  );
  const response = await fetch(resolved.url, {
    ...resolved.init,
    method: "POST",
    signal,
    headers: { ...resolved.init.headers, "content-type": "application/json" },
    body: JSON.stringify(guidePayload(request)),
  });
  return { response, resolved };
}

/**
 * Streams one answer. `onText` receives the whole answer so far each time more
 * arrives; the promise resolves with the final text.
 */
export async function askGuide(
  request: GuideAskRequest,
  signal: AbortSignal,
  onText: (text: string) => void,
): Promise<string> {
  let attempt: Awaited<ReturnType<typeof post>>;
  try {
    attempt = await post(request, signal, false);
  } catch (error) {
    if (signal.aborted) throw error;
    // Could not even connect to the API directly: hold it off and use the proxy.
    markDomainApiUnreachable();
    attempt = await post(request, signal, true);
  }
  if (attempt.response.status === 401 && attempt.resolved.direct) {
    invalidateDomainApiSession(attempt.resolved.token ?? undefined);
    attempt = await post(request, signal, true);
  }
  const { response } = attempt;
  if (response.status === 429) throw new GuideAskError("rate-limited");
  if (!response.ok || !response.body) throw new GuideAskError("unavailable");

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let text = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    text += value;
    onText(text);
  }
  if (text.trim().length === 0) throw new GuideAskError("unavailable");
  return text;
}
